import React, { ReactNode, useEffect, useState } from 'react';
import classNames from 'classnames';
import Icon from 'components/atoms/Icon';
import Button, { ButtonProps } from 'components/atoms/Button';
import Popover, { PopoverPlacement } from '@idui/react-popover';
import { IconName } from 'components/atoms/Icon/all';
import { useIsMobile } from 'utils/hooks';

interface MoreOptionsDropDownProps {
  arrowOffset?: number;
  arrowSize?: number;
  buttons: ButtonProps[];
  placement?: PopoverPlacement;
  mobilePlacement?: PopoverPlacement;
  reactNodeButtons?: ReactNode | ReactNode[];
  content?: ReactNode;
  className?: string;
  iconClassName?: string;
  cardClassName?: string;
  popoverClassName?: string;
  openerIcon?: IconName;
  width?: number;
  disabled?: boolean;
}

function MoreOptionsDropDown({
  arrowOffset,
  arrowSize = 12,
  buttons,
  reactNodeButtons,
  placement,
  content,
  className,
  iconClassName,
  cardClassName,
  popoverClassName,
  openerIcon = 'more-vertical',
  width,
  disabled,
  mobilePlacement,
}: MoreOptionsDropDownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const isMobile = useIsMobile();

  useEffect(() => {
    if (disabled) setIsOpen(false);
  }, [disabled, isOpen]);

  useEffect(() => {
    if (isOpen) {
      const closePopOver = () => {
        setIsOpen(false);
      };
      document.body.addEventListener('click', closePopOver);
      return () => document.body.removeEventListener('click', closePopOver);
    }
  }, [isOpen]);

  return (
    <Popover
      className={classNames('!bg-white !rounded-sm !p-3', popoverClassName)}
      arrowSize={arrowSize}
      arrowOffset={arrowOffset}
      width={width ? `${width}px` : '250px'}
      isOpen={isOpen}
      placement={isMobile && mobilePlacement ? mobilePlacement : placement || PopoverPlacement.left}
      mouseLeaveDelay={250}
      content={
        <div className={classNames('relative shadow-lg', cardClassName)}>
          <div className="relative">
            {reactNodeButtons ||
              buttons.filter(Boolean).map((btn, i) => {
                if (btn.hidden) return <React.Fragment key={`buttons_${i}`} />;
                return (
                  <Button
                    key={`buttons_${i}`}
                    {...btn}
                    onClick={() => {
                      setIsOpen(false);
                      btn.onClick?.();
                    }}
                    type="transparent"
                    hideHighlight
                    className={classNames(
                      'w-full whitespace-nowrap my-2.5 text-sm font-semibold text-glooCharcoal hover:text-primary-700',
                      btn.className,
                    )}
                  />
                );
              })}
          </div>
        </div>
      }
    >
      <div
        onClick={() => {
          if (!disabled) {
            setIsOpen(true);
          }
        }}
        className={classNames(
          'flex flex-row items-center justify-center cursor-pointer text-glooMistyGray hover:text-glooMedGray ',
          { 'w-8': !content },
          className,
        )}
      >
        {content || <Icon name={openerIcon} size={20} className={classNames('ml-2 rounded-full p-2', iconClassName)} />}
      </div>
    </Popover>
  );
}

export default MoreOptionsDropDown;
